// Team — manage other admins. Owner-only view. Add admins, change roles
// or scopes, disable/enable. The backend prevents owners from demoting or
// disabling themselves, so we don't need to duplicate that guard here.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { ApiError } from '../lib/api';

interface AdminRow {
  id: string;
  email: string;
  scopes: string[];
  role: 'owner' | 'staff';
  added_by_email: string | null;
  added_at: string;
  disabled_at: string | null;
}

const KNOWN_SHOPS = [
  'portal.marketdwellings.com',
  'marketdwellings.com',
];

async function fetchAdmins(): Promise<{ admins: AdminRow[] }> {
  const cfg = (window as unknown as { __MD_STUDIO_ADMIN_CONFIG__: { apiBase: string } })
    .__MD_STUDIO_ADMIN_CONFIG__;
  const res = await fetch(`${cfg.apiBase}/storefront/portal-admin/admins`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.error ?? 'fetch_failed', body.message ?? res.statusText);
  }
  return res.json();
}

async function patchAdmin(id: string, body: Record<string, unknown>): Promise<void> {
  const cfg = (window as unknown as { __MD_STUDIO_ADMIN_CONFIG__: { apiBase: string } })
    .__MD_STUDIO_ADMIN_CONFIG__;
  const res = await fetch(`${cfg.apiBase}/storefront/portal-admin/admins/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.error ?? 'patch_failed', body.message ?? res.statusText);
  }
}

async function createAdmin(body: { email: string; scopes: string[]; role: 'owner' | 'staff' }): Promise<void> {
  const cfg = (window as unknown as { __MD_STUDIO_ADMIN_CONFIG__: { apiBase: string } })
    .__MD_STUDIO_ADMIN_CONFIG__;
  const res = await fetch(`${cfg.apiBase}/storefront/portal-admin/admins`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.error ?? 'create_failed', body.message ?? res.statusText);
  }
}

export function TeamView() {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const adminsQuery = useQuery({ queryKey: ['admins'], queryFn: fetchAdmins });

  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      patchAdmin(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admins'] });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : (err as Error).message),
  });

  return (
    <div className="view">
      <header className="view-head">
        <div>
          <h1>Team</h1>
          <p className="sub">Owners and staff with admin access.</p>
        </div>
        <div className="view-head__actions">
          <button className="btn btn--primary" onClick={() => setShowAdd(true)}>
            <Plus size={14} /> Add admin
          </button>
        </div>
      </header>

      {error && <div className="error-banner">{error}</div>}

      {adminsQuery.isLoading ? (
        <div className="state">Loading…</div>
      ) : adminsQuery.isError ? (
        <div className="state error">{(adminsQuery.error as Error).message}</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>Scopes</th>
              <th>Added</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {adminsQuery.data!.admins.map((a) => (
              <tr key={a.id} className={a.disabled_at ? 'is-dimmed' : ''}>
                <td>{a.email}</td>
                <td>
                  <select
                    value={a.role}
                    onChange={(e) => patchMut.mutate({ id: a.id, body: { role: e.target.value } })}
                  >
                    <option value="owner">owner</option>
                    <option value="staff">staff</option>
                  </select>
                </td>
                <td>
                  {a.scopes.map((s) => (
                    <span key={s} className="tag tag--soft">
                      {s}
                    </span>
                  ))}
                </td>
                <td title={`by ${a.added_by_email ?? '—'}`}>
                  {new Date(a.added_at).toLocaleDateString()}
                </td>
                <td>{a.disabled_at ? 'disabled' : 'active'}</td>
                <td>
                  <button
                    className="btn btn--small"
                    onClick={() =>
                      patchMut.mutate({
                        id: a.id,
                        body: { disabled: !a.disabled_at },
                      })
                    }
                  >
                    {a.disabled_at ? 'Enable' : 'Disable'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showAdd && (
        <AddAdminModal
          onClose={() => setShowAdd(false)}
          onSaved={() => {
            setShowAdd(false);
            void qc.invalidateQueries({ queryKey: ['admins'] });
          }}
        />
      )}
    </div>
  );
}

function AddAdminModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'owner' | 'staff'>('staff');
  const [scopes, setScopes] = useState<Set<string>>(new Set(['portal.marketdwellings.com']));
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () => createAdmin({ email, role, scopes: [...scopes] }),
    onSuccess: onSaved,
    onError: (err) => setError(err instanceof ApiError ? err.message : (err as Error).message),
  });

  function toggleScope(s: string) {
    const next = new Set(scopes);
    if (next.has(s)) next.delete(s);
    else next.add(s);
    setScopes(next);
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Add admin</h3>
        {error && <div className="error-banner">{error}</div>}
        <label>
          Email
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@marketdwellings.com" />
        </label>
        <label>
          Role
          <select value={role} onChange={(e) => setRole(e.target.value as 'owner' | 'staff')}>
            <option value="staff">staff</option>
            <option value="owner">owner</option>
          </select>
        </label>
        <fieldset>
          <legend>Shop scopes</legend>
          {KNOWN_SHOPS.map((s) => (
            <label key={s} className="checkbox">
              <input type="checkbox" checked={scopes.has(s)} onChange={() => toggleScope(s)} />
              <span>{s}</span>
            </label>
          ))}
        </fieldset>
        <div className="modal__actions">
          <button onClick={onClose}>Cancel</button>
          <button
            className="btn btn--primary"
            disabled={!email || scopes.size === 0 || createMut.isPending}
            onClick={() => createMut.mutate()}
          >
            {createMut.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
